import { Badge, Card, CardBody } from "reactstrap"
import ThemePlaceholder from "components/Common/ThemePlaceholder"
import ProfileImageUploader from "components/Common/ProfileImageUploader"
import UserAccountEditor from "components/Common/UserAccountEditor"
import { formatReadableDate } from "utils/formatDate"

const detailRows = user => [
  { label: "Username", value: user?.username || "-" },
  { label: "Email", value: user?.email || "-" },
  {
    label: "Account Type",
    value: user?.is_system ? "System User" : "Standard User",
  },
  { label: "Showroom", value: user?.showroom?.name || "-" },
  {
    label: "Linked Employee",
    value: user?.employee?.employee_id
      ? `${user.employee.name} (#${user.employee.employee_id})`
      : user?.employee?.name || "-",
  },
  {
    label: "Created",
    value: user?.created_at ? formatReadableDate(user.created_at) : "-",
  },
]

const coverStyle = {
  background: "linear-gradient(135deg, #f7f9ff 0%, #eef3ff 52%, #e4ecff 100%)",
  borderBottom: "1px solid rgba(85, 110, 230, 0.12)",
}

const coverTitleStyle = {
  color: "#2f3b63",
}

const coverSubtitleStyle = {
  color: "#6c7aa5",
}

export default function UserProfileCard({
  user,
  statusMeta,
  loading = false,
  action = null,
  showImageUploader = false,
  showProfileEditor = true,
  onProfileSaved,
}) {
  const rows = detailRows(user)

  return (
    <Card className="border-0 shadow-sm overflow-hidden mb-4">
      <div className="px-4 pt-4 pb-5" style={coverStyle}>
        {loading ? (
          <ThemePlaceholder
            style={{
              width: 140,
              height: 28,
              borderRadius: 8,
              backgroundColor: "rgba(85,110,230,.12)",
            }}
          />
        ) : (
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <div>
              <h5 className="mb-1" style={coverTitleStyle}>
                User Profile
              </h5>
              <p className="mb-0" style={coverSubtitleStyle}>
                Account identity and access overview
              </p>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {action}
              {showProfileEditor && !loading ? (
                <UserAccountEditor
                  user={user}
                  onSaved={onProfileSaved}
                  size="sm"
                  outline
                />
              ) : null}
              <Badge
                className={`badge-soft-${statusMeta?.color || "secondary"} border-0`}
              >
                {statusMeta?.label || "-"}
              </Badge>
            </div>
          </div>
        )}
      </div>

      <CardBody className="p-4" style={{ marginTop: -42 }}>
        <div className="d-flex align-items-end gap-3 mb-4">
          {showImageUploader && !loading ? (
            <ProfileImageUploader user={user} size={104} className="flex-shrink-0" />
          ) : (
            <div
              className="rounded-4 overflow-hidden border border-3 border-white bg-white shadow-sm flex-shrink-0"
              style={{ width: 104, height: 104 }}
            >
              {loading ? (
                <ThemePlaceholder
                  style={{ width: "100%", height: "100%", borderRadius: 0 }}
                />
              ) : (
                <div
                  className="w-100 h-100 d-flex align-items-center justify-content-center text-primary fw-bold"
                  style={{ fontSize: 36, background: "#eef2ff" }}
                >
                  {(user?.name || user?.username || "U").charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          )}

          <div className="min-width-0 flex-grow-1">
            {loading ? (
              <>
                <ThemePlaceholder
                  style={{ width: 220, height: 26, borderRadius: 8 }}
                />
                <ThemePlaceholder
                  className="mt-2"
                  style={{ width: 180, height: 18, borderRadius: 8 }}
                />
                <ThemePlaceholder
                  className="mt-2"
                  style={{ width: 140, height: 18, borderRadius: 8 }}
                />
              </>
            ) : (
              <>
                <h4 className="mb-1 text-truncate">{user?.name || "User"}</h4>
                <p className="text-muted mb-1 text-truncate">
                  @{user?.username || "-"}
                </p>
                <p className="text-muted mb-0 text-truncate">
                  {user?.showroom?.name || "No showroom assigned"}
                </p>
              </>
            )}
          </div>
        </div>

        <div className="specs-list">
          {rows.map(item => (
            <div
              key={item.label}
              className="d-flex justify-content-between py-2 border-bottom gap-3"
            >
              <span className="text-muted">{item.label}:</span>
              {loading ? (
                <ThemePlaceholder
                  style={{ width: 140, height: 18, borderRadius: 4 }}
                />
              ) : (
                <span className="fw-medium text-end">{item.value}</span>
              )}
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  )
}
